import React from "react";

import css from "./Input.module.css";

import cn from "classnames";
import { useSyncedState } from "@/utils/useSyncedState";
import { isNotNil } from "@/utils/typeGuards";

type Props = {
    className?: string;
    placeholder?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    hasError?: boolean;
    width?: "small";
};

export const Input: React.FC<React.PropsWithChildren<Props>> = ({
    className,
    placeholder,
    value,
    onChange,
    hasError = false,
    width,
}) => {
    return (
        <input
            className={cn(
                css.main,
                className,
                hasError && css.error,
                width === "small" && css.smallWidth
            )}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
        />
    );
};

type InputNumberProps = {
    className?: string;
    placeholder?: string;
    value?: number;
    step?: number;
    min?: number;
    max?: number;
    onBlur?: (value: number) => void | number;
    hasError?: boolean;
    width?: "small";
};

export const InputNumber: React.FC<React.PropsWithChildren<InputNumberProps>> = ({
    className,
    placeholder,
    value,
    onBlur,
    step = 1,
    min,
    max,
    hasError = false,
    width,
}) => {
    const [localValue, setLocalValue] = useSyncedState(value);

    const triggerOnBlur = () => {
        if (!onBlur || !isNotNil(localValue)) return;

        const result = onBlur(localValue);
        if (typeof result === "number" && result !== localValue) {
            setLocalValue(result);
        }
    };

    const handleBlur = () => {
        triggerOnBlur();
    };

    const handleClick = () => {
        triggerOnBlur();
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && isNotNil(localValue)) {
            triggerOnBlur();
        }
    };

    return (
        <input
            className={cn(
                css.main,
                className,
                hasError && css.error,
                width === "small" && css.smallWidth
            )}
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => setLocalValue(Number(e.target.value))}
            onBlur={handleBlur}
            onClick={handleClick}
            type="number"
            step={step}
            min={min}
            max={max}
            onKeyDown={handleKeyPress}
        />
    );
};
